import { css, html, PropertyValues, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { waitFor } from '../modules/async';
import { KryptonConstants } from '../modules/krypton_constants';
import { ICONS } from '../modules/resources';
import { KryptonElement } from './krypton-element';
import { KryptonEventDetail } from './krypton-event-detail';

export class MenuJson {
    menuObjectList: any;
}

export class MenuItemJsonData {
    description: string;
    label: string;
    activation: {
        actionOnClick: string
    };
    dropdown: any
}

export class Menu {

    menu: MenuItem[] = [];

    add(menuItem: MenuItem) {
        this.menu.push(menuItem);
    }

    *iterator(): IterableIterator<MenuItem> {
        for (let i = 0; i < this.menu.length; i++)
            yield this.menu[i];
    }

    [Symbol.iterator]() {
        return this.iterator();
    }

}

export class MenuItem extends KryptonElement {

    _label = "";
    _tag;
    _disabled: boolean = false;
    _category: boolean = false;
    clickProtection: { enabled: boolean, active: boolean, buffer: number, timerHandle: any } = { enabled: true, active: false, buffer: 300, timerHandle: 0 };
    action: string;
    data: Object;
    @property({ type: String, reflect: true }) accessor title;

    // constructor(data: { description: string, label: string, activation: { activationOnClick: string } }, options?: Object[]) {
    constructor(data?: MenuItemJsonData, options?: MenuItemJsonData[]) {
        super();
        if (!data) return;
        this.data = data;
        this.label = data.label;
        this.title = data.description;
        this.action = data.activation?.actionOnClick;
        if (options && options.length) {
            options.forEach(o => this.appendChild(new MenuItem(o)));
        } else {
            // Menu like Ask Envoy is like a category menu, but it has action and no children!
            let categorWithLink: any = data;
            let action = categorWithLink?.detail?.activation?.actionOnClick;
            if (action) {
                this.action = action;
            }
        }
    }

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener("click", this.onClick);
    }

    disconnectedCallback() {
        this.removeEventListener("click", this.onClick);
        super.disconnectedCallback();
    }

    onClick(e: Event): boolean {

        e.stopPropagation();

        if (this.disabled)
            return false;

        if (this.clickProtection.enabled) {
            if (this.clickProtection.active) {
                console.log("click protected!");
                this.clearClickProtection();
                return false;
            }
            this.clickProtection.active = true;
            this.clearClickProtection();
        }

        if (this.category) {
            this.toggleOptions();
            return true;

        } else {
            this.doAction();
            return true;
        }

    }

    clearClickProtection() {
        clearTimeout(this.clickProtection.timerHandle);
        this.clickProtection.timerHandle = setTimeout(() => {
            this.clickProtection.active = false;
            this.clickProtection.timerHandle = 0;
        }, this.clickProtection.buffer);
    }

    doAction(multiplePages: boolean = false) {
        this.fire(KryptonEventDetail.MENU_ITEM_EVENT, new KryptonEventDetail(KryptonEventDetail.CLICK, KryptonEventDetail.MENU_ITEM_EVENT, { action: this.action, multiplePages: multiplePages }));
    }

    async toggleOptions(open?: boolean) {
        if (this.classList.contains("animating"))
            return;
        if (this.classList.contains("open")) {
            this.collapseOptions();
        } else {
            // First, let's collapse all open menus, then expand current menu
            let currentOpenMenus = (this.parentNode || this.parentElement)?.querySelectorAll("menu-item.open");
            if (currentOpenMenus && currentOpenMenus.length) {
                // There is at least one open menu, so collapse all of them
                currentOpenMenus.forEach((e: any) => {
                    if (e != this && e && e.collapseOptions) {
                        e.collapseOptions();
                    }
                })
            }
            this.expandOptions();
        }
    }

    async expandOptions() {
        if (!this.category)
            return;
        this.classList.add("animating");
        const options = this.shadowRoot?.getElementById("options") as HTMLElement;
        this.classList.add("open");
        options.classList.add("wake");
        const gainedHeight = options.scrollHeight;
        options.style.setProperty("height", gainedHeight + "px");
        this.fire(KryptonEventDetail.MENU_ITEM_EVENT, new KryptonEventDetail(KryptonEventDetail.EXPAND, KryptonEventDetail.MENU_ITEM_EVENT, { open: true, height: gainedHeight }));
        await waitFor(KryptonConstants.showHideAnimationTime);
        this.classList.remove("animating");
    }

    async collapseOptions() {
        if (!this.category)
            return;
        this.classList.add("animating");
        const options = this.shadowRoot?.getElementById("options") as HTMLElement;
        const lostHeight = options.scrollHeight;
        options.style.setProperty("height", "0px");
        this.classList.remove("open");
        this.fire(KryptonEventDetail.MENU_ITEM_EVENT, new KryptonEventDetail(KryptonEventDetail.COLLAPSE, KryptonEventDetail.MENU_ITEM_EVENT, { open: false, height: lostHeight }));
        await waitFor(KryptonConstants.showHideAnimationTime);
        options.classList.remove("wake");
        options.style.removeProperty("height");
        this.classList.remove("animating");
        await waitFor(KryptonConstants.showHideAnimationTime);
        this.querySelectorAll("menu-item.category.open").forEach(m => (m as MenuItem).collapseOptions());
    }

    async reveal() {
        this.parents.forEach(p => p.expandOptions());
        await waitFor(KryptonConstants.revealAnimationTime);
    }

    highlight(highlight: boolean = true) {
        this.classList[highlight ? "add" : "remove"]("highlight");
        this.parents.forEach(p => p.highlight(highlight));
    }

    flatten(flatten: boolean = true) {
        this.classList[flatten ? "add" : "remove"]("flatten");
    }

    get label() {
        return this._label;
    }

    @property({ type: String })
    set label(label) {
        const oldValue = this._label;
        this._label = label;
        this.tag = label;
        this.requestUpdate("label", oldValue);
    }

    firstUpdated(changedPropertes: PropertyValues) {
        super.firstUpdated(changedPropertes);
        this.setIcon();
    }

    setIcon() {
        if (this.parentElement instanceof MenuItem) return;
        const normalizedLabel = this.label.replace(/[\W,%]/g, "").toLowerCase(), icon = "/resources/menu/" + normalizedLabel + "/" + normalizedLabel + ".svg";
        this.shadowRoot?.getElementById("label")?.style.setProperty("--bg-image-url", "url('" + icon + "')");
    }

    get tag() {
        return this._tag;
    }

    @property({ type: String, reflect: true })
    set tag(tag) {
        const oldValue = this._tag;
        this._tag = tag.toLowerCase();
        setTimeout(() => this.requestUpdate("tag", oldValue), 0); //-- required to update tag via label setter
    }

    get category() {
        return this._category;
    }

    @property({ type: Boolean })
    set category(category) {
        const oldValue = this._category;
        this._category = category;
        category ? this.classList.add("category") : this.classList.remove("category");
        this.requestUpdate("category", oldValue);
    }

    get disabled() {
        return this._disabled;
    }

    @property({ type: Boolean, reflect: true })
    set disabled(disabled) {
        const oldValue = this._disabled;
        this._disabled = disabled;
        this.classList[disabled ? "add" : "remove"]("disabled");
        this.requestUpdate("disabled", oldValue);
    }

    get options() {
        const slot = this.shadowRoot?.getElementById("options")?.querySelector("slot");
        return slot?.assignedNodes().filter(n => n instanceof MenuItem) as MenuItem[];
    }

    protected onMenuItemEvent(e: CustomEvent) {
        if (KryptonEventDetail.MENU_ITEM_EVENT === e.detail.name) {
            let kryptonEventDetail: KryptonEventDetail = e.detail;
            let props = kryptonEventDetail.getProperties();
            if (!props.height) {
                return;
            }
            const options = this.shadowRoot?.getElementById("options") as HTMLElement
                , newHeight = props.open ? options.scrollHeight + props.height : options.scrollHeight - props.height;
            options.style.setProperty("height", newHeight + "px"); //-- subtract 3 becuase of padding
        }
    }

    protected onSlotChange(e: Event) {
        const slot = this.shadowRoot?.querySelector("slot")
            , options = slot?.assignedNodes().filter(n => n instanceof MenuItem) as MenuItem[];
        if (options.length) {
            this.category = true;
            options.forEach(o => o.classList.add("option"));
        }
    }

    get parents(): MenuItem[] {
        const parents: MenuItem[] = [];
        let parent = this.parentElement;
        while (parent && parent instanceof MenuItem) {
            parents.push(parent);
            parent = parent.parentElement;
        }
        return parents;
    }

    static styles = css`
        :host {
            display: block;
            text-align: left;
        }

        :host(.disabled) {
            opacity: 0.35;
            pointer-events: none;
            cursor: default;
        }

        #label {
            display: -ms-grid;
            display: grid;
            cursor: pointer;
            padding: 0.5rem 5px 0.5rem 0.5rem;
            -ms-grid-columns: 2.7em 1fr auto;
            grid-template-columns: 2.7em 1fr auto;
            grid-template-areas: "icon label caret";
        }

        #label > span {
            grid-area: label;
            -ms-grid-row: 1;
            -ms-grid-column: 2;
            grid-row: 1;
            grid-column: 2;
        }

        #label::before {
            -ms-grid-row: 1;
            -ms-grid-column: 1;
            grid-row: 1;
            grid-column: 1;
            grid-area: icon;
            display: block;
            content: '';
            background-position: center center;
            background-repeat: no-repeat;
        }

        :host(.category) > #label::before {
            background-image: var(--bg-image-url, url('${unsafeCSS(ICONS.circle.file)}'));
        }

        :host(:not(.category)) > #label::before {
            background-image: var(--bg-image-url, url('${unsafeCSS(ICONS.blank.file)}'));
        }

        :host(.category) > #label::after {
            -ms-grid-row: 1;
            -ms-grid-column: 3;
            grid-row: 1;
            grid-column: 3;
            grid-area: caret;
            content: '';
            display: block;
            height: 20px;
            width: 20px;
            margin-right: 0.4em;
            background: url('/resources/menu/collapsed.png') right center no-repeat; 
            background-size: 0.8em 0.9em;
            transition: transform 125ms ease-out;
        }

        :host(.category.open) > #label::after {
            -webkit-transform: rotate(90deg);
            -moz-transform: rotate(90deg);
            -ms-transform: rotate(90deg);
            -o-transform: rotate(90deg);
            transform: rotate(90deg);
        }

        :host(.flatten) #options {
            display: block;
            height: auto !important;
        }

        #options {
            display: none;
            transition: height 150ms ease-out;
            background-color: #ededed;
            -webkit-box-shadow: inset 0px 3px 2px 0px rgba(0, 0, 0, 0.2);
            -moz-box-shadow: inset 0px 3px 2px 0px rgba(0, 0, 0, 0.2);
            box-shadow: inset 0px 3px 2px 0px rgba(0, 0, 0, 0.2);
            background: rgba(255, 255, 255, 0.9);
            height: 0;
        }

        #options.wake {
            display: block;
            overflow: hidden;
        }

        :host(.option) {
            padding-left: 0;
            background-color: #eee;
        }

        :host(.option) #label:hover {
            text-decoration: underline;
        }

        :host(.search-hit-parent) {
            pointer-events: none;
        }

        :host(.search-hit-parent) #options{
            pointer-events: auto;
        }

        :host(.search-hit-parent) #label {
            opacity: 0.35;
            pointer-events: none;
            cursor: default;
        }

        :host(.search-hit-parent) #label::after {
            display: none;
        }

        :host(.search-miss:not(.search-hit-parent)) {
            display: none;
        }

        :host(.highlight) {
            font-weight: bold;
            color: #16b595;
        }

        :host(.highlight) ::slotted(menu-item:not(.highlight)) {
            color: var(--krypton-color-black);
            font-weight: normal;
        }

    `;

    render() {
        return html`
        <label id="label">
            <span>${this.label}</span>
        </label>
        <div id="options" @click="${this.handleOptionsClick}" class="${this.label.toLowerCase()}-options" @menu-item-event="${this.onMenuItemEvent}">
            <slot @slotchange="${this.onSlotChange}"></slot>
        </div>`;
    }

    handleOptionsClick(e) {
        // This happens only when the user clicks on a disabled menu - it just gets triggered in the container div.  So let's don't propagate this
        e.stopPropagation();
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'menu-item': MenuItem;
    }
}
